import React, { useEffect } from "react";
import Modal from "Components/UI/Modal/Modal";
import { useSelector } from "react-redux";
import {
  selectFaultsCodesList,
  selectFaultsListIsFuncLoading,
} from "Redux/Faults/slice";
import { Controller, useForm } from "react-hook-form";
import { IAddFaultsRule, IFaultsCode, IFaultsRule } from "Shared/Types/faults";
import { yupResolver } from "@hookform/resolvers/yup";
import { faultRuleSchema } from "Shared/Utils/yup/faultRule";
import InputField from "Components/UI/Inputs/InputField";
import RadioGroup from "Components/UI/RadioGroup/RadioGroup";
import styles from "./FaultRuleForm.module.scss";
import { useAppDispatch } from "Redux/store";
import {
  addFaultRule,
  getFaultCodesList,
  updateFaultRule,
} from "Redux/Faults/reducer";
import Button from "Components/UI/Button/Button";
import { notifyError, notifySuccess } from "Components/Notify/notify";
import { faultRulesInitialValues } from "Shared/Constants/faults";
import SelectFieldNew from "Components/UI/Inputs/SelectFieldNew";

type FaultRuleFormProps = {
  open: boolean;
  onClose: () => void;
  type: "EDIT" | "CREATE";
  rule?: IFaultsRule;
};

const FaultRuleForm: React.FC<FaultRuleFormProps> = ({
  open,
  onClose,
  type,
  rule,
}) => {
  const dispatch = useAppDispatch();
  const codeList = useSelector(selectFaultsCodesList);
  const isFuncLoading = useSelector(selectFaultsListIsFuncLoading);

  const { control, handleSubmit, setValue, reset } = useForm({
    resolver: yupResolver(faultRuleSchema),
    defaultValues: faultRulesInitialValues,
  });

  const PRIORITY_OPTIONS = [
    { label: "Off", value: "off" },
    { label: "Ignore Code", value: "ignore" },
    { label: "Flag as Critical", value: "critical" },
  ];

  const handlePriorityStatusChange = (_e: unknown, value: string) => {
    return setValue("priority_status", value);
  };
  const onSuccess = () => {
    notifySuccess("Fault Rule successfully created!");
  };

  const onError = () => {
    notifyError("Something wrong! Perhaps the fault rule does exist");
  };

  const onCreateFaultRule = async (data: IAddFaultsRule) => {
    if (type === "EDIT" && rule) {
      await dispatch(updateFaultRule({ ...data, id: rule?.id })).unwrap();
      notifySuccess("Fault rule Successfully updated!");
    } else {
      await dispatch(addFaultRule({ data, onError, onSuccess })).unwrap();
    }
    reset(faultRulesInitialValues);
    onClose();
  };

  useEffect(() => {
    if (type === "EDIT" && rule) {
      reset({ ...rule, code: rule.code.toString() });
    }
  }, [rule]);

  return (
    <Modal
      isClosed
      title={type === "CREATE" ? "Add Fault Rule" : "Edit Fault Rule"}
      open={open}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit(onCreateFaultRule)} className={styles.form}>
        <SelectFieldNew<IFaultsCode, IAddFaultsRule>
          optionLabel="code"
          optionValue="code"
          control={control}
          name="code"
          label="Code"
          options={codeList}
          withController
          required
          isAsyncPaginated
          asyncLoadOptionType="code"
        />

        <InputField label="Name" name="name" withController control={control} />
        <InputField
          label="Description"
          name="description"
          withController
          control={control}
          multiline
          rows={3}
        />
        <div>
          <Controller
            name="priority_status"
            control={control}
            defaultValue={rule?.priority_status ?? ""}
            render={({ field }) => (
              <RadioGroup
                {...field}
                isRequired
                label="Priority Handling"
                className={styles.form__radio}
                options={PRIORITY_OPTIONS}
              />
            )}
          />
        </div>
        <div className={styles.form__buttons}>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button isFuncLoading={isFuncLoading} type="submit">
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default FaultRuleForm;
